import React, { useState, useRef, useEffect, useMemo } from 'react';
import { 
  DndContext, 
  DragOverlay, 
  closestCorners, 
  KeyboardSensor, 
  PointerSensor, 
  useSensor, 
  useSensors, 
  DragStartEvent, 
  DragOverEvent, 
  DragEndEvent,
  defaultDropAnimationSideEffects,
  DropAnimation
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useReactToPrint } from 'react-to-print';
import { Aggregator, MenuData, User } from '../../types';
import { apiClient } from '../../services/apiClient';
import { MenuEditor } from './MenuEditor';
import { MenuCanvas } from './MenuCanvas';
import { OrderingPage } from './OrderingPage';
import { MenuDashboard } from './MenuDashboard';
import { Dashboard as BistroDashboard } from '../bistro-builder/components/Dashboard';
import { MenuDetails } from '../bistro-builder/components/MenuDetails';
import { MenuData as BistroMenuData } from '../bistro-builder/types';
import { getMenu, saveMenu, createMenu as createBistroMenu } from '../bistro-builder/services/cloudflareService';
import { ToastProvider as BistroToastProvider } from '../bistro-builder/components/ToastProvider';
import { ConfirmationModal } from '../ConfirmationModal';
import { Printer, Save, Download, Upload, Play, ArrowLeft, X } from 'lucide-react';
import { exportToExcel, importFromExcel } from '@/services/excelService';
import { exportMenuBundle } from '@/services/menuExport';
import { analyzeMenuHealth } from '@/services/menuHealth';
import { normalizeMenuData, summarizeMenu } from '@/services/menuNormalize';
import { loadDraft, saveDraft, clearDraft, computeHash } from '@/services/menuDraft';
import { exportMenuToJsonFile, importMenuFromJsonFile } from '@/services/menuJson';
import { ToastContainer, ToastMessage } from '../Toast';

const dropAnimation: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
        styles: {
            active: {
                opacity: '0.5',
            },
        },
    }),
};

interface MenuBuilderWorkspaceProps {
  normalizedData: MenuData;
  setNormalizedData: React.Dispatch<React.SetStateAction<MenuData>>;
  focusTarget: { id: string; ts: number } | null;
  setFocusTarget: React.Dispatch<React.SetStateAction<{ id: string; ts: number } | null>>;
  currency: string;
  language: 'en' | 'ar';
  sensors: ReturnType<typeof useSensors>;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragOver: (event: DragOverEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  activeId: string | null;
  printRef: React.RefObject<HTMLDivElement>;
  healthFilter: string | null;
  healthIssueLabel?: string;
  healthHighlight: { categoryIds: Set<string>; itemIds: Set<string> };
  onClearHealthFilter: () => void;
  requestConfirmation: (options: { title: string; message: string; onConfirm: () => void; type?: 'confirm' | 'success' | 'danger' }) => void;
  onValidityChange: (valid: boolean) => void;
  onShowToast: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

const MenuBuilderWorkspace: React.FC<MenuBuilderWorkspaceProps> = ({
  normalizedData,
  setNormalizedData,
  focusTarget,
  setFocusTarget,
  currency,
  language,
  sensors,
  handleDragStart,
  handleDragOver,
  handleDragEnd,
  activeId,
  printRef,
  healthFilter,
  healthIssueLabel,
  healthHighlight,
  onClearHealthFilter,
  requestConfirmation,
  onValidityChange,
  onShowToast,
}) => {
  const [activeSection, setActiveSection] = useState<'categories' | 'items' | 'options'>('categories');

  return (
    <DndContext 
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
    >
        <div className="px-4 py-3 border-b border-slate-200 bg-white flex items-center gap-2">
          {(['categories','items','options'] as const).map((section) => {
            const labels: Record<typeof section, string> = { categories: 'Categories', items: 'Items', options: 'Options' };
            const active = activeSection === section;
            return (
              <button
                key={section}
                onClick={() => setActiveSection(section)}
                className={`px-3.5 py-2 rounded-lg text-sm font-semibold border transition-all ${active ? 'bg-slate-900 text-white border-slate-900 shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:text-slate-900 hover:border-slate-300'}`}
              >
                {labels[section]}
              </button>
            );
          })}
        </div>

        <div className="px-4 py-4 border-b border-slate-100 bg-white">
          {activeSection === 'categories' && (
            <div className="text-sm text-slate-700">
              <div className="font-bold text-slate-900 mb-1">Categories</div>
              <div className="text-slate-600">Organize your menu into sections and set their display order.</div>
            </div>
          )}
          {activeSection === 'items' && (
            <div className="text-sm text-slate-700">
              <div className="font-bold text-slate-900 mb-1">Items</div>
              <div className="text-slate-600">Add and edit items within each category, including prices and details.</div>
            </div>
          )}
          {activeSection === 'options' && (
            <div className="text-sm text-slate-700">
              <div className="font-bold text-slate-900 mb-1">Options</div>
              <div className="text-slate-600">Manage item options and choice groups to tailor selections.</div>
            </div>
          )}
        </div>

        <div className="flex flex-1 h-full overflow-hidden">
          {/* Editor Sidebar */}
          <div className="w-[600px] shrink-0 h-full overflow-hidden z-10 shadow-xl">
              <MenuEditor 
                data={normalizedData} 
                setData={setNormalizedData} 
                  focusTarget={focusTarget}
                  onCategoryClick={(id) => setFocusTarget({ id, ts: Date.now() })}
                  currency={currency}
                  requestConfirmation={requestConfirmation}
                  healthFilterKey={healthFilter}
                  healthIssueLabel={healthIssueLabel}
                  healthHighlight={healthHighlight}
                  onClearHealthFilter={onClearHealthFilter}
                  onValidityChange={onValidityChange}
                  onShowToast={onShowToast}
              />
          </div>

          {/* Preview Canvas */}
          <div className="flex-1 h-full overflow-hidden bg-slate-100 relative">
              <MenuCanvas 
                data={normalizedData} 
                  language={language} 
                  printRef={printRef}
                  onCategoryClick={(id) => setFocusTarget({ id, ts: Date.now() })}
                  onItemClick={(id) => setFocusTarget({ id, ts: Date.now() })}
                  focusTarget={focusTarget}
                  currency={currency}
              />
          </div>
        </div>

        <DragOverlay dropAnimation={dropAnimation}>
            {activeId ? (
               <div className="bg-white p-4 rounded shadow-xl border border-blue-500 opacity-90 w-64">
                   Dragging Item...
               </div>
            ) : null}
        </DragOverlay>
    </DndContext>
  );
};

interface MenuBuilderProps {
  currentUser: User;
}

export const MenuBuilder: React.FC<MenuBuilderProps> = ({ currentUser }) => {
  const [data, setData] = useState<MenuData>(normalizeMenuData({ title: { en: 'Loading...' }, categories: [] }));
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [language, setLanguage] = useState<'en' | 'ar'>('en');
  const [focusTarget, setFocusTarget] = useState<{ id: string; ts: number } | null>(null);
  const [currency, setCurrency] = useState('QAR');
  const [viewMode, setViewMode] = useState<'dashboard' | 'workspace'>('dashboard');
  const [dashboardMode, setDashboardMode] = useState<'sterling' | 'bistro'>('sterling');
  const [bistroView, setBistroView] = useState<'dashboard' | 'details'>('dashboard');
  const [bistroMenuId, setBistroMenuId] = useState<string | null>(null);
  const [bistroMenuName, setBistroMenuName] = useState<string>('');
  const [bistroBrandId, setBistroBrandId] = useState<string | null>(null);
  const [bistroData, setBistroData] = useState<BistroMenuData | null>(null);
  const [activeTab, setActiveTab] = useState<'builder' | 'live' | 'final'>('builder');
  const [currentMenuId, setCurrentMenuId] = useState<string | null>(null);
  const [healthFilter, setHealthFilter] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [lastSavedHash, setLastSavedHash] = useState<string>('');
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    type: 'confirm' | 'success' | 'danger';
    title: string;
    message: string;
    onConfirm?: () => void;
  }>({ isOpen: false, type: 'confirm', title: '', message: '' });
  const [draftPrompt, setDraftPrompt] = useState<{ menu: MenuData; hash: string; savedAt?: number } | null>(null);
  const [pendingImport, setPendingImport] = useState<{ menu: MenuData; source: 'json' | 'excel'; summary: string } | null>(null);
  const [aggregators, setAggregators] = useState<Aggregator[]>([]);
  const eligibleAggregators = useMemo(
    () => (aggregators || []).filter((a) => a.imageProfile?.enabled && (a.imageProfile?.variants || []).length > 0),
    [aggregators]
  );
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportIncludeExcel, setExportIncludeExcel] = useState(true);
  const [exportIncludeImageColumn, setExportIncludeImageColumn] = useState(true);
  const [exportIncludeOriginals, setExportIncludeOriginals] = useState(true);
  const [exportFolderByCategory, setExportFolderByCategory] = useState(true);
  const [exportIncludeFlat, setExportIncludeFlat] = useState(false);
  const [exportIncludeVariants, setExportIncludeVariants] = useState(true);
  const [exportMode, setExportMode] = useState<'full' | 'images-only'>('full');
  const [selectedAggregatorIds, setSelectedAggregatorIds] = useState<Set<string>>(new Set());
  const [exportProgress, setExportProgress] = useState<{ phase: string; completed: number; total: number; imagesDone: number; imagesTotal: number; variantsDone: number; variantsTotal: number } | null>(null);
  const cancelExportRef = useRef<boolean>(false);
  const [exportConfirm, setExportConfirm] = useState<{ open: boolean; count: number } | null>(null);
  const draftSaveTimer = useRef<number | null>(null);
  const draftHandledRef = useRef<boolean>(false);

  const setNormalizedData: React.Dispatch<React.SetStateAction<MenuData>> = (updater) => {
    setData((prev) => {
      const current = normalizeMenuData(prev);
      const next = typeof updater === 'function' ? (updater as (value: MenuData) => MenuData)(current) : updater;
      return normalizeMenuData(next);
    });
  };

  const pushToast = (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => {
    const toast: ToastMessage = { id: `toast-${Date.now()}-${Math.random()}`, type, title, message };
    setToasts((prev) => [...prev, toast]);
  };

  const removeToast = (id: string) => setToasts((prev) => prev.filter((t) => t.id !== id));

  const fileInputRef = useRef<HTMLInputElement>(null);
  const jsonFileInputRef = useRef<HTMLInputElement>(null);
  const normalizedData = useMemo(() => normalizeMenuData(data), [data]);
  const healthAnalysis = useMemo(() => analyzeMenuHealth(normalizedData), [normalizedData]);
  const activeHealthIssue = useMemo(
    () => healthAnalysis.issues.find((i) => i.key === healthFilter),
    [healthAnalysis, healthFilter]
  );
  const healthHighlight = useMemo(
    () => ({
      categoryIds: new Set(activeHealthIssue?.categoryIds || []),
      itemIds: new Set(activeHealthIssue?.itemIds || []),
    }),
    [activeHealthIssue]
  );
  
  useEffect(() => {
    if (viewMode === 'dashboard') return;

    if (!currentMenuId) {
        const fresh = normalizeMenuData({ title: { en: 'New Menu' }, categories: [] });
        setNormalizedData(fresh);
        setLastSavedHash(computeHash(fresh));
        setDirty(false);
        setIsLoading(false);
        return;
    }

    const loadMenu = async () => {
      setIsLoading(true);
      try {
        const menu = await apiClient.menuBuilder.getMenu(currentUser.id, currentMenuId || undefined);
        const normalized = normalizeMenuData(menu);
        if (normalized.id) {
            setCurrentMenuId(normalized.id);
        }
        setNormalizedData(normalized);
        setLastSavedHash(computeHash(normalized));
        setDirty(false);
      } catch (err) {
        console.error('Failed to load menu', err);
        pushToast('error', 'Load Failed', 'Could not load menu. Starting a new draft.');
        const fallback = normalizeMenuData({ title: { en: 'New Menu' }, categories: [] });
        setNormalizedData(fallback);
        setLastSavedHash(computeHash(fallback));
      } finally {
        setIsLoading(false);
      }
    };
    loadMenu();
  }, [currentUser.id, currentMenuId, viewMode]);

  const handleSelectMenu = (menuId: string) => {
      setCurrentMenuId(menuId);
      setViewMode('workspace');
      setActiveTab('builder');
      setDirty(false);
      setLastSavedHash('');
      draftHandledRef.current = false;
  };

  const updateHealthParam = (key: string | null) => {
    setHealthFilter(key);
    const params = new URLSearchParams(window.location.search);
    if (key) {
      params.set('health', key);
    } else {
      params.delete('health');
    }
    const qs = params.toString();
    const nextUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
    window.history.replaceState(null, '', nextUrl);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const key = params.get('health');
    setHealthFilter(key);
  }, []);

  useEffect(() => {
    const loadAggregators = async () => {
      try {
        const res = await apiClient.aggregators.list();
        setAggregators(res || []);
        const defaults = (res || []).filter((a) => a.imageProfile?.enabled && (a.imageProfile?.variants || []).length > 0).map((a) => a.id);
        setSelectedAggregatorIds(new Set(defaults));
      } catch (err) {
        console.warn('Failed to load aggregators for export', err);
      }
    };
    loadAggregators();
  }, []);

  useEffect(() => {
    draftHandledRef.current = false;
  }, [currentMenuId, viewMode]);

  useEffect(() => {
    if (viewMode === 'dashboard' || isLoading) return;
    if (draftHandledRef.current) return;
    const draft = loadDraft(currentUser.id, currentMenuId);
    const currentHash = computeHash(normalizedData);
    if (draft && draft.hash !== currentHash) {
        setDraftPrompt(draft);
        draftHandledRef.current = true;
    }
  }, [viewMode, isLoading, normalizedData, currentMenuId, currentUser.id]);

  useEffect(() => {
    if (viewMode === 'dashboard' || isLoading) return;
    const currentHash = computeHash(normalizedData);
    setDirty(currentHash !== lastSavedHash);

    if (draftSaveTimer.current) {
        window.clearTimeout(draftSaveTimer.current);
    }
    draftSaveTimer.current = window.setTimeout(() => {
        saveDraft(currentUser.id, currentMenuId, normalizedData);
    }, 500) as unknown as number;

    return () => {
        if (draftSaveTimer.current) {
            window.clearTimeout(draftSaveTimer.current);
        }
    };
  }, [normalizedData, viewMode, isLoading, currentUser.id, currentMenuId, lastSavedHash]);

  const handleCreateMenu = () => {
      setCurrentMenuId(null);
      setNormalizedData({ title: { en: 'New Menu' }, categories: [] });
      setIsLoading(false);
      setViewMode('workspace');
      setActiveTab('builder');
      setLastSavedHash('');
      setDirty(false);
      draftHandledRef.current = false;
  };

  const clearHealthFilter = () => updateHealthParam(null);

  const handleRestoreDraft = () => {
      if (draftPrompt) {
          setNormalizedData(draftPrompt.menu);
          setDirty(true);
      }
      setDraftPrompt(null);
      draftHandledRef.current = true;
      pushToast('info', 'Draft Restored', 'Recovered your last unsaved changes.');
  };

  const handleDiscardDraft = () => {
      clearDraft(currentUser.id, currentMenuId);
      setDraftPrompt(null);
      draftHandledRef.current = true;
      pushToast('info', 'Draft Discarded', 'Continuing with the saved menu.');
  };

  const handleBackToDashboard = () => {
      setViewMode('dashboard');
      setCurrentMenuId(null);
      setActiveTab('builder');
  };

  const handleSave = async () => {
    if (!isValid) {
      pushToast('warning', 'Fix validation', 'Please resolve highlighted fields before saving.');
      setActiveTab('builder');
      return;
    }
    setModalState({
        isOpen: true,
        type: 'confirm',
        title: 'Save Menu Changes?',
        message: 'Are you sure you want to save the current menu configuration? This will update the live menu.',
        onConfirm: async () => {
            setModalState(prev => ({ ...prev, isOpen: false }));
            setIsSaving(true);
            try {
                const savedMenu = await apiClient.menuBuilder.saveMenu(currentUser.id, { ...normalizedData, id: currentMenuId || undefined });
                const normalizedMenu = normalizeMenuData(savedMenu.menu);
                if (normalizedMenu.id) {
                    setCurrentMenuId(normalizedMenu.id);
                }
                setNormalizedData(normalizedMenu);
                const newHash = computeHash(normalizedMenu);
                setLastSavedHash(newHash);
                setDirty(false);
                clearDraft(currentUser.id, normalizedMenu.id || currentMenuId);
                clearDraft(currentUser.id, null);
                setModalState({
                    isOpen: true,
                    type: 'success',
                    title: 'Menu Saved!',
                    message: 'Your menu has been successfully saved and updated.',
                    onConfirm: undefined
                });
                pushToast('success', 'Menu Saved', 'Changes synced to the server.');
            } catch (err) {
                console.error('Failed to save menu', err);
                setModalState({
                    isOpen: true,
                    type: 'danger',
                    title: 'Save Failed',
                    message: 'There was an error saving your menu. Please try again.',
                    onConfirm: undefined
                });
                pushToast('error', 'Save Failed', 'Could not persist your changes.');
            } finally {
                setIsSaving(false);
            }
        }
    });
  };

  const requestConfirmation = (options: { title: string; message: string; onConfirm: () => void; type?: 'confirm' | 'success' | 'danger' }) => {
      setModalState({
          isOpen: true,
          type: options.type || 'confirm',
          title: options.title,
          message: options.message,
          onConfirm: () => {
              options.onConfirm();
              setModalState(prev => ({ ...prev, isOpen: false }));
          }
      });
  };

  const componentRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
  });

  const handleFixIssue = (issueKey: string) => {
    updateHealthParam(issueKey);
    setViewMode('workspace');
    setActiveTab('builder');
  };

  const findContainer = (id: string) => {
    if (data.categories.find(c => c.id === id)) return 'root';
    return data.categories.find(c => c.items.find(i => i.id === id))?.id;
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    const overId = over?.id;

    if (!overId || active.id === overId) return;

    const activeContainer = findContainer(active.id as string);
    let overContainer = findContainer(overId as string);

    if (activeContainer && activeContainer !== 'root' && overContainer === 'root') {
       // If dragging an item over a category, treat it as dragging over that category's container
       if (data.categories.find(c => c.id === overId)) {
           overContainer = overId as string;
       }
    }

    if (
      !activeContainer || 
      !overContainer || 
      activeContainer === 'root' || 
      overContainer === 'root'
    ) {
      return;
    }

    if (activeContainer !== overContainer) {
      setNormalizedData((prev) => {
        const activeItems = prev.categories.find(c => c.id === activeContainer)?.items || [];
        const overItems = prev.categories.find(c => c.id === overContainer)?.items || [];
        const activeIndex = activeItems.findIndex(i => i.id === active.id);
        const overIndex = overItems.findIndex(i => i.id === overId);

        let newIndex;
        if (prev.categories.find(c => c.id === overId)) {
          newIndex = overItems.length + 1;
        } else {
          const isBelowOverItem =
            over &&
            active.rect.current.translated &&
            active.rect.current.translated.top >
              over.rect.top + over.rect.height;

          const modifier = isBelowOverItem ? 1 : 0;
          newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
        }

        const newCategories = prev.categories.map(c => {
            if (c.id === activeContainer) {
                return { ...c, items: c.items.filter(i => i.id !== active.id) };
            }
            if (c.id === overContainer) {
                const item = activeItems[activeIndex];
                const newItems = [...c.items];
                newItems.splice(newIndex, 0, item);
                return { ...c, items: newItems };
            }
            return c;
        });

        return { ...prev, categories: newCategories };
      });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const activeContainer = findContainer(active.id as string);
    const overContainer = findContainer(over?.id as string);

    if (
      activeContainer &&
      overContainer &&
      activeContainer === overContainer
    ) {
      if(activeContainer === 'root') {
          const activeIndex = data.categories.findIndex(c => c.id === active.id);
          const overIndex = data.categories.findIndex(c => c.id === over?.id);
          if (activeIndex !== overIndex) {
                setNormalizedData(prev => ({
                  ...prev,
                  categories: arrayMove(prev.categories, activeIndex, overIndex)
              }));
          }
      } else {
          const categoryIndex = data.categories.findIndex(c => c.id === activeContainer);
          const activeIndex = data.categories[categoryIndex].items.findIndex(i => i.id === active.id);
          const overIndex = data.categories[categoryIndex].items.findIndex(i => i.id === over?.id);
          
          if (activeIndex !== overIndex) {
              const newCategories = [...data.categories];
              newCategories[categoryIndex].items = arrayMove(newCategories[categoryIndex].items, activeIndex, overIndex);
              setNormalizedData(prev => ({ ...prev, categories: newCategories }));
          }
      }
    }

    setActiveId(null);
  };

  const handleExportExcel = () => {
      try {
          exportToExcel(normalizedData, 'menu-export', currency);
          pushToast('success', 'Excel Exported', 'Menu downloaded as Excel.');
      } catch (err) {
          console.error(err);
          pushToast('error', 'Export Failed', 'Could not export Excel.');
      }
  };

  const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
          try {
              const importedData = normalizeMenuData(await importFromExcel(e.target.files[0]));
              const summary = summarizeMenu(importedData);
              setPendingImport({
                  menu: importedData,
                  source: 'excel',
                  summary: summary.title + ' | ' + summary.categories + ' categories | ' + summary.items + ' items',
              });
          } catch (err) {
              console.error(err);
              pushToast('error', 'Import Failed', 'Could not read the Excel file.');
          }
          // Reset input
          if (fileInputRef.current) fileInputRef.current.value = '';
      }
  };

  const handleExportJson = () => {
      try {
          exportMenuToJsonFile(normalizedData, (normalizedData.title.en || 'menu').replace(/\s+/g, '-').toLowerCase());
          pushToast('success', 'JSON Exported', 'Menu downloaded as JSON.');
      } catch (err) {
          console.error(err);
          pushToast('error', 'Export Failed', 'Could not export JSON.');
      }
  };

  const handleImportJson = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
          const importedData = normalizeMenuData(await importMenuFromJsonFile(file));
          const summary = summarizeMenu(importedData);
          setPendingImport({
              menu: importedData,
              source: 'json',
              summary: summary.title + ' | ' + summary.categories + ' categories | ' + summary.items + ' items',
          });
      } catch (err) {
          console.error(err);
          pushToast('error', 'Import Failed', 'Invalid JSON file.');
      } finally {
          if (jsonFileInputRef.current) jsonFileInputRef.current.value = '';
      }
  };

  const applyPendingImport = () => {
      if (!pendingImport) return;
      setNormalizedData(pendingImport.menu);
      setDirty(true);
      setActiveTab('builder');
      pushToast('success', 'Menu Imported', pendingImport.source === 'json' ? 'Imported menu from JSON.' : 'Imported menu from Excel.');
      setPendingImport(null);
  };

  const cancelPendingImport = () => setPendingImport(null);

  const toggleAggregatorSelection = (id: string) => {
    setSelectedAggregatorIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const confirmAndExport = () => {
    setExportConfirm(null);
    handleExportBundle(true);
  };

  const handleExportBundle = async (skipGuard = false) => {
    if (isExporting) return;
    const selectedAggs = exportIncludeVariants ? eligibleAggregators.filter((agg) => selectedAggregatorIds.has(agg.id)) : [];
    const itemsWithImgs = itemsWithImages;
    const variantsPerItem = selectedAggs.reduce((acc, agg) => acc + (agg.imageProfile?.enabled ? (agg.imageProfile?.variants || []).length : 0), 0);
    if (variantsPerItem > 10) {
      console.warn('High variant count per item detected:', variantsPerItem);
    }
    const totalImages = (exportIncludeOriginals ? itemsWithImgs : 0) + (itemsWithImgs * variantsPerItem);
    if (!skipGuard && totalImages > 300) {
      setExportConfirm({ open: true, count: totalImages });
      return;
    }
    cancelExportRef.current = false;
    setIsExporting(true);
    setExportProgress({ phase: 'preparing', completed: 0, total: 1, imagesDone: 0, imagesTotal: 0, variantsDone: 0, variantsTotal: 0 });
    try {
      const result = await exportMenuBundle(normalizedData, {
        includeExcel: exportMode === 'full' && exportIncludeExcel,
        includeImageColumn: exportIncludeImageColumn,
        includeOriginalImages: exportIncludeOriginals,
        folderByCategory: exportFolderByCategory,
        includeFlatFolder: exportIncludeFlat,
        aggregators: selectedAggs,
        currency,
        onProgress: (p) => setExportProgress(p),
        onCancelCheck: () => cancelExportRef.current,
      });
      const variantNote = result.failedVariants > 0 ? ` (${result.failedVariants} variant images skipped)` : '';
      const origNote = result.failedImages > 0 ? ` (${result.failedImages} originals skipped)` : '';
      if (result.cancelled) {
        pushToast('info', 'Export cancelled', `Processed ${result.itemsWithImages} items${origNote}${variantNote}`);
      } else {
        pushToast('success', 'Export Ready', `Downloaded ZIP with Excel and images. Items: ${result.totalItems}, with images: ${result.itemsWithImages}${origNote}${variantNote}`);
      }
      setExportModalOpen(false);
    } catch (err) {
      console.error(err);
      pushToast('error', 'Export Failed', 'Could not generate export bundle.');
    } finally {
      setIsExporting(false);
      setExportProgress(null);
      cancelExportRef.current = false;
      setExportConfirm(null);
    }
  };

  const summary = summarizeMenu(normalizedData);
  const itemsWithImages = useMemo(() => {
    let count = 0;
    (normalizedData.categories || []).forEach((cat) => {
      (cat.items || []).forEach((item) => {
        if (item.image) count += 1;
      });
    });
    return count;
  }, [normalizedData]);

  if (viewMode === 'dashboard') {
      return (
          <>
            <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg">
                    <button 
                        onClick={() => setDashboardMode('sterling')}
                        className={`px-4 py-1.5 text-sm font-bold rounded-md transition-all ${dashboardMode === 'sterling' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        Current Builder
                    </button>
                    <button 
                        onClick={() => setDashboardMode('bistro')}
                        className={`px-4 py-1.5 text-sm font-bold rounded-md transition-all ${dashboardMode === 'bistro' ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        Bistro Builder
                    </button>
                </div>
            </div>

            {dashboardMode === 'sterling' ? (
                <MenuDashboard 
                    userId={currentUser.id} 
                    onSelectMenu={handleSelectMenu} 
                    onCreateMenu={handleCreateMenu} 
                    onShowToast={pushToast}
                />
            ) : (
                <BistroToastProvider>
                    {bistroView === 'dashboard' ? (
                        <BistroDashboard 
                            initialBrandId={bistroBrandId}
                            onBrandSelect={setBistroBrandId}
                            onSelectMenu={async (id, name) => {
                                setBistroMenuId(id);
                                setBistroMenuName(name);
                                try {
                                    const data = await getMenu(id);
                                    if (data) {
                                        setBistroData(data);
                                        setBistroView('details');
                                    }
                                } catch (err) {
                                    console.error(err);
                                    pushToast('error', 'Failed to load menu', 'Could not fetch menu data');
                                }
                            }}
                            onCreateMenu={async (brandId) => {
                                try {
                                    const newMenu = await createBistroMenu(brandId, 'New Menu');
                                    setBistroMenuId(newMenu.id);
                                    setBistroMenuName(newMenu.name);
                                    setBistroData({ title: { en: newMenu.name }, categories: [], status: 'live' });
                                    setBistroView('details');
                                } catch (err) {
                                    console.error(err);
                                    pushToast('error', 'Failed to create menu', 'Could not create new menu');
                                }
                            }}
                        />
                    ) : (
                        bistroData && (
                            <MenuDetails 
                                menuName={bistroMenuName}
                                data={bistroData}
                                onBack={() => {
                                    setBistroView('dashboard');
                                    // Ensure we stay on the same brand if possible, or just go back to dashboard view
                                }}
                                onStatusChange={() => {}}
                                onOpenEditor={() => {}}
                                onUpdateData={(newData) => {
                                    setBistroData(newData);
                                    if (bistroMenuId) {
                                        saveMenu(bistroMenuId, newData);
                                    }
                                }}
                                onExport={() => {
                                    if (bistroData) {
                                        exportToExcel(bistroData as any, bistroMenuName || 'menu-export');
                                    }
                                }}
                            />
                        )
                    )}
                </BistroToastProvider>
            )}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
          </>
      );
  }

  return (
    <div className="menu-builder-compact flex flex-col h-full w-full overflow-hidden bg-white text-slate-900 text-[13px] leading-relaxed">
      {/* Header */}
      <div className="h-16 border-b border-slate-200 flex items-center justify-between px-4 bg-white z-20 shadow-sm shrink-0">
        <div className="flex items-center gap-3">
          <button 
              onClick={handleBackToDashboard}
              className="p-2 hover:bg-slate-100 rounded-full text-slate-500 transition-colors"
              title="Back to Dashboard"
          >
              <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-lg font-bold text-slate-800">Menu Builder</h1>
            <p className="text-[12px] text-slate-500">{summary.categories} categories Â· {summary.items} items</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-2 border ${dirty ? 'border-amber-200 text-amber-700 bg-amber-50' : 'border-emerald-200 text-emerald-700 bg-emerald-50'}`}>
            <span className={`w-2 h-2 rounded-full ${dirty ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500'}`}></span>
            {dirty ? 'Unsaved changes' : 'All changes saved'}
          </div>
          <button 
              onClick={handleSave}
              disabled={isSaving || !isValid}
              className="flex items-center gap-1.5 px-3.5 py-1.5 text-[13px] font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors disabled:opacity-50"
          >
              <Save size={18} /> {isSaving ? 'Saving...' : 'Save Menu'}
          </button>
        </div>
      </div>

      {/* Tabs + actions */}
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {(['builder','live','final'] as const).map((tab) => {
            const labels: Record<typeof tab, string> = { builder: 'Builder', live: 'Live Preview', final: 'Final View' };
            const active = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-all ${active ? 'bg-white border-slate-300 shadow-sm text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-white'}`}
              >
                {labels[tab]}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-white border border-slate-200 rounded-lg overflow-hidden">
              <button 
                  onClick={() => setLanguage('en')}
                  className={`px-3 py-1 text-[12px] font-bold transition-all ${language === 'en' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                  EN
              </button>
              <button 
                  onClick={() => setLanguage('ar')}
                  className={`px-3 py-1 text-[12px] font-bold transition-all ${language === 'ar' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                  AR
              </button>
          </div>
          <input 
              type="file" 
              ref={jsonFileInputRef} 
              className="hidden" 
              accept=".json,application/json" 
              onChange={handleImportJson} 
          />
          <input 
              type="file" 
              ref={fileInputRef} 
              className="hidden" 
              accept=".xlsx, .xls" 
              onChange={handleImportExcel} 
          />
          <button 
              onClick={() => jsonFileInputRef.current?.click()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold text-slate-600 hover:text-slate-800 hover:bg-white rounded-lg transition-colors border border-slate-200"
          >
              <Upload size={16} /> Import JSON
          </button>
          <button 
              onClick={handleExportJson}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold text-slate-600 hover:text-slate-800 hover:bg-white rounded-lg transition-colors border border-slate-200"
          >
              <Download size={16} /> Export JSON
          </button>
          <button 
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold text-slate-600 hover:text-slate-800 hover:bg-white rounded-lg transition-colors border border-slate-200"
          >
              <Upload size={16} /> Import Excel
          </button>
          <button 
              onClick={() => setExportModalOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors border border-slate-900"
          >
              <Download size={16} /> Export
          </button>
          <button 
              onClick={() => handlePrint && handlePrint()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold text-slate-600 hover:text-slate-800 hover:bg-white rounded-lg transition-colors border border-slate-200"
          >
              <Printer size={16} /> Print / PDF
          </button>
        </div>
      </div>

      {healthFilter && activeHealthIssue && (
        <div className="mx-4 my-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3 flex items-center justify-between">
          <div className="text-sm font-semibold">
            Filtering by health issue: <span className="font-bold">{activeHealthIssue.label}</span>
          </div>
          <button
            onClick={clearHealthFilter}
            className="text-xs font-bold px-3 py-1.5 rounded-md border border-amber-200 hover:bg-amber-100"
          >
            Clear filter
          </button>
        </div>
      )}

      <div className="flex-1 overflow-hidden">
        {activeTab === 'builder' && (
          <MenuBuilderWorkspace
            normalizedData={normalizedData}
            setNormalizedData={setNormalizedData}
            focusTarget={focusTarget}
            setFocusTarget={setFocusTarget}
            currency={currency}
            language={language}
            sensors={sensors}
            handleDragStart={handleDragStart}
            handleDragOver={handleDragOver}
            handleDragEnd={handleDragEnd}
            activeId={activeId}
            printRef={componentRef}
            healthFilter={healthFilter}
            healthIssueLabel={activeHealthIssue?.label}
            healthHighlight={healthHighlight}
            onClearHealthFilter={clearHealthFilter}
            requestConfirmation={requestConfirmation}
            onValidityChange={setIsValid}
            onShowToast={pushToast}
          />
        )}

        {activeTab === 'live' && (
          <OrderingPage 
            data={normalizedData} 
              onBack={() => setActiveTab('builder')} 
              currency={currency}
              onFixIssue={handleFixIssue}
              variant="embedded"
          />
        )}

        {activeTab === 'final' && (
          <FinalMenuView 
            data={normalizedData}
            language={language}
            currency={currency}
            printRef={componentRef}
          />
        )}
      </div>

      <ConfirmationModal 
        isOpen={modalState.isOpen}
        type={modalState.type}
        title={modalState.title}
        message={modalState.message}
        onConfirm={modalState.onConfirm}
        onCancel={() => setModalState(prev => ({ ...prev, isOpen: false }))}
        confirmText="Yes, Save Menu"
        cancelText="Cancel"
      />

      <ConfirmationModal 
        isOpen={!!draftPrompt}
        type="confirm"
        title="Restore unsaved draft?"
        message={draftPrompt ? `A draft saved at ${draftPrompt.savedAt ? new Date(draftPrompt.savedAt).toLocaleString() : 'recently'} was found. Restore it?` : ''}
        onConfirm={handleRestoreDraft}
        onCancel={handleDiscardDraft}
        confirmText="Restore Draft"
        cancelText="Discard"
      />

      <ConfirmationModal 
        isOpen={!!pendingImport}
        type="confirm"
        title="Import menu?"
        message={pendingImport ? `Replace current menu with ${pendingImport.summary}?` : ''}
        onConfirm={applyPendingImport}
        onCancel={cancelPendingImport}
        confirmText="Replace"
        cancelText="Cancel"
      />

      {exportModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Export Menu</h3>
                <p className="text-sm text-slate-500">Download Excel and image bundles for platforms.</p>
              </div>
              <button onClick={() => setExportModalOpen(false)} className="text-slate-500 hover:text-slate-700 rounded-lg p-2">
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                  <p className="text-xs font-semibold text-slate-500 uppercase">Total Items</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{summary.items}</p>
                </div>
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                  <p className="text-xs font-semibold text-slate-500 uppercase">Items with images</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{itemsWithImages}</p>
                </div>
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                  <p className="text-xs font-semibold text-slate-500 uppercase">Categories</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{summary.categories}</p>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-bold text-slate-800">Export Mode</h4>
                <div className="flex gap-2">
                  <button
                    className={`px-3 py-2 rounded-lg border text-sm font-semibold ${exportMode === 'full' ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-700 hover:bg-slate-100'}`}
                    onClick={() => { setExportMode('full'); setExportIncludeExcel(true); }}
                  >
                    Excel + Images
                  </button>
                  <button
                    className={`px-3 py-2 rounded-lg border text-sm font-semibold ${exportMode === 'images-only' ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-700 hover:bg-slate-100'}`}
                    onClick={() => { setExportMode('images-only'); setExportIncludeExcel(false); }}
                  >
                    Images Only
                  </button>
                </div>
                {exportMode === 'full' && (
                  <div className="flex flex-col gap-2 pt-2">
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                      <input
                        type="checkbox"
                        checked={exportIncludeExcel}
                        onChange={(e) => setExportIncludeExcel(e.target.checked)}
                      />
                      Include Excel (.xlsx)
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={exportIncludeImageColumn}
                        onChange={(e) => setExportIncludeImageColumn(e.target.checked)}
                      />
                      Include image filename columns
                    </label>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-bold text-slate-800">Images</h4>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <input type="checkbox" checked={exportIncludeOriginals} onChange={(e) => setExportIncludeOriginals(e.target.checked)} />
                    Include original images
                  </label>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <input type="checkbox" checked={exportIncludeVariants} onChange={(e) => setExportIncludeVariants(e.target.checked)} />
                    Include per-aggregator variants
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={exportFolderByCategory} onChange={(e) => setExportFolderByCategory(e.target.checked)} />
                    Organize by category folders
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={exportIncludeFlat} onChange={(e) => setExportIncludeFlat(e.target.checked)} />
                    Also include flat "all_images/" folder
                  </label>
                </div>

                <div className="mt-4 space-y-2">
                  <p className="text-sm font-semibold text-slate-700">Aggregator variants</p>
                  {exportIncludeVariants && eligibleAggregators.length === 0 && (
                    <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-3">
                      No platforms found. You can still export originals.
                    </div>
                  )}
                  {exportIncludeVariants && (
                    <div className="space-y-3">
                      {eligibleAggregators.map((agg) => {
                        const variants = agg.imageProfile?.enabled ? (agg.imageProfile?.variants || []) : [];
                        const hasVariants = variants.length > 0;
                        const summarySizes = variants.map((v) => `${v.width}x${v.height}`).join(', ');
                        return (
                          <div key={agg.id} className="border border-slate-200 rounded-xl p-3">
                            <div className="flex items-center justify-between">
                              <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                                <input
                                  type="checkbox"
                                  disabled={!hasVariants}
                                  checked={selectedAggregatorIds.has(agg.id)}
                                  onChange={() => toggleAggregatorSelection(agg.id)}
                                />
                                {agg.name}
                              </label>
                              <span className="text-xs text-slate-500">
                                {hasVariants ? `${variants.length} variant${variants.length > 1 ? 's' : ''} • ${summarySizes}` : 'No variants'}
                              </span>
                            </div>
                            {hasVariants && (
                              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                                {variants.map((variant, idx) => (
                                  <div key={`${agg.id}-${idx}`} className="border border-slate-100 rounded-lg p-2 bg-slate-50">
                                    <div className="text-sm font-semibold text-slate-800">{variant.label || variant.key}</div>
                                    <div className="text-xs text-slate-500">{variant.width} x {variant.height} {variant.format ? `• ${variant.format.toUpperCase()}` : ''} {variant.quality ? `• q${variant.quality}` : ''}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-3">
              {!isExporting ? (
                <>
                  <button
                    onClick={() => setExportModalOpen(false)}
                    className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-white"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleExportBundle}
                    className="px-4 py-2 rounded-lg bg-slate-900 text-white font-semibold hover:bg-slate-800 flex items-center gap-2"
                  >
                    {exportMode === 'full' ? 'Download ZIP (Excel + Images)' : 'Download ZIP (Images Only)'}
                  </button>
                </>
              ) : (
                <div className="w-full flex flex-col gap-3">
                  <div className="flex items-center justify-between text-sm font-semibold text-slate-700">
                    <span>{exportProgress?.phase === 'excel' ? 'Exporting Excel' : exportProgress?.phase === 'images' ? 'Processing Images' : exportProgress?.phase === 'zipping' ? 'Zipping' : 'Preparing'}</span>
                    <span>{Math.round(((exportProgress?.completed || 0) / (exportProgress?.total || 1)) * 100)}%</span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-slate-200 overflow-hidden">
                    <div
                      className="h-full bg-slate-900 transition-all"
                      style={{ width: `${Math.min(100, Math.round(((exportProgress?.completed || 0) / (exportProgress?.total || 1)) * 100))}%` }}
                    />
                  </div>
                  <div className="text-xs text-slate-600 flex flex-wrap gap-3">
                    <span>Images: {exportProgress?.imagesDone || 0}/{exportProgress?.imagesTotal || 0}</span>
                    <span>Variants: {exportProgress?.variantsDone || 0}/{exportProgress?.variantsTotal || 0}</span>
                  </div>
                  <div className="flex justify-end">
                    <button
                      onClick={() => { cancelExportRef.current = true; }}
                      className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-white"
                    >
                      Cancel Export
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {exportConfirm?.open && (
        <ConfirmationModal
          isOpen={true}
          type="confirm"
          title="Large export"
          message={`This export will generate approximately ${exportConfirm.count} images and may take time. Continue?`}
          onConfirm={confirmAndExport}
          onCancel={() => setExportConfirm(null)}
          confirmText="Continue"
          cancelText="Cancel"
        />
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}

const FinalMenuView: React.FC<{ data: MenuData; language: 'en' | 'ar'; currency: string; printRef: React.RefObject<HTMLDivElement> }> = ({ data, language, currency, printRef }) => {
  const isArabic = language === 'ar';
  const categories = Array.isArray(data.categories) ? data.categories : [];
  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div ref={printRef} className="max-w-6xl mx-auto p-6 md:p-10">
        <div className="text-center mb-10">
          <h2 className={`text-3xl font-black text-slate-900 ${isArabic ? 'font-arabic' : ''}`}>
            {isArabic ? (data.title.ar || data.title.en) : data.title.en}
          </h2>
          <div className="mt-2 text-sm text-slate-500">{categories.length} sections</div>
        </div>
        <div className="space-y-8">
          {categories.map((cat) => (
            <div key={cat.id} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
                <h3 className={`text-xl font-bold text-slate-900 uppercase tracking-wide ${isArabic ? 'font-arabic' : ''}`}>
                  {isArabic ? (cat.name.ar || cat.name.en) : cat.name.en}
                </h3>
                <span className="text-xs text-slate-400 font-semibold">{cat.items?.length || 0} items</span>
              </div>
              <div className="space-y-4">
                {(cat.items || []).map((item) => (
                  <div key={item.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 p-3 rounded-xl border border-slate-100 bg-slate-50/50">
                    <div className="md:col-span-2">
                      <div className="w-full aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-100 flex items-center justify-center relative">
                        {item.image ? (
                          <img 
                            src={item.image} 
                            alt={item.name.en} 
                            className="w-full h-full object-cover"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; const ph = (e.target as HTMLImageElement).nextElementSibling as HTMLElement; if (ph) ph.classList.remove('hidden'); }}
                          />
                        ) : null}
                        <div className={`absolute inset-0 flex items-center justify-center text-[11px] text-slate-400 ${item.image ? 'hidden' : ''}`}>
                          No image
                        </div>
                      </div>
                    </div>
                    <div className="md:col-span-7">
                      <div className="flex items-baseline justify-between gap-2">
                        <h4 className={`text-lg font-bold text-slate-900 ${isArabic ? 'font-arabic' : ''}`}>
                          {isArabic ? (item.name.ar || item.name.en) : item.name.en}
                        </h4>
                        <div className="text-base font-bold text-slate-800">{currency} {Number(item.price || 0).toFixed(2)}</div>
                      </div>
                      <p className={`text-sm text-slate-600 mt-1 ${isArabic ? 'font-arabic' : ''}`}>
                        {isArabic ? (item.description.ar || item.description.en) : item.description.en || 'No description'}
                      </p>
                      {item.choiceGroups?.length ? (
                        <div className="mt-3 space-y-2">
                          {item.choiceGroups.map((group) => (
                            <div key={group.id}>
                              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                                {isArabic ? (group.name.ar || group.name.en) : group.name.en}
                              </div>
                              <div className="text-xs text-slate-500">
                                {group.min ? 'Min ' + group.min : 'Optional'}{group.max ? ' | Max ' + group.max : ''}
                              </div>
                              <div className="flex flex-wrap gap-2 mt-1">
                                {(group.options || []).map((opt) => (
                                  <span key={opt.id} className="text-xs text-slate-700 bg-white border border-slate-200 rounded-full px-2 py-0.5">
                                    {isArabic ? (opt.name.ar || opt.name.en) : opt.name.en}{opt.priceModifier ? ` (+${currency} ${opt.priceModifier.toFixed(2)})` : ''}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <div className="md:col-span-3 flex items-center md:justify-end">
                      <div className="w-full md:w-auto text-sm text-slate-500">
                        Last updated: {new Date().toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
                {!cat.items?.length && (
                  <div className="text-sm text-slate-500 bg-white border border-dashed border-slate-200 rounded-xl p-4 text-center">
                    No items in this section.
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
